/*
 * ELASTICSEARCH CONFIDENTIAL
 * __________________
 *
 *  Copyright Elasticsearch B.V. All rights reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Elasticsearch B.V. and its suppliers, if any.
 * The intellectual and technical concepts contained herein
 * are proprietary to Elasticsearch B.V. and its suppliers and
 * may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright
 * law.  Dissemination of this information or reproduction of
 * this material is strictly forbidden unless prior written
 * permission is obtained from Elasticsearch B.V.
 */
import React, { Fragment, useState } from 'react'
import { defineMessages } from 'react-intl'

import {
  EuiButton,
  EuiButtonEmpty,
  EuiCallOut,
  EuiCodeBlock,
  EuiDescriptionList,
  EuiFieldText,
  EuiFlexGroup,
  EuiFlexItem,
  EuiFlyout,
  EuiFlyoutBody,
  EuiFlyoutFooter,
  EuiFlyoutHeader,
  EuiForm,
  EuiFormRow,
  EuiSpacer,
  EuiText,
  EuiTitle,
  useGeneratedHtmlId,
} from '@elastic/eui'

import { FormattedMessage } from '@modules/i18n'
import useUpdateBillingAccount from '@modules/billing-lib/mutations/useUpdateBillingAccount'

import type { AllProps } from './types'
import type { FunctionComponent } from 'react'

const messages = defineMessages({
  changeBillingAccountTitle: {
    id: `marketplace-settings.gcp-change-billing-account-title`,
    defaultMessage: `Change Billing Account`,
  },
  changeBillingAccountDescription: {
    id: `marketplace-settings.gcp-change-billing-account-description`,
    defaultMessage: `Change the Billing Account of a GCP Marketplace Organization.
    Enter at least one of the new details provided by the customer.`,
  },
  organizationId: {
    id: `marketplace-settings.gcp-organization-id`,
    defaultMessage: `Organization ID`,
  },
  orderNumber: {
    id: `marketplace-settings.gcp-order-number`,
    defaultMessage: `Order Number`,
  },
  orderNumberHelpText: {
    id: `marketplace-settings.gcp-order-number-help-text`,
    defaultMessage: `The new Order Number, also known as Entitlement ID
    (example: 98d76e5e-476f-321d-aefb-f8c90710bbc7).`,
  },
  billingAccountId: {
    id: `marketplace-settings.gcp-billing-account-id`,
    defaultMessage: `Billing ID`,
  },
  billingAccountIdHelpText: {
    id: `marketplace-settings.gcp-billing-account-id-help-text`,
    defaultMessage: 'The new Billing Account ID (example: 012A34-FB5C67-D8E6F9).',
  },
  cancel: {
    id: `marketplace-settings.gcp-change-billing-account-button.cancel`,
    defaultMessage: `Cancel`,
  },
  close: {
    id: `marketplace-settings.gcp-change-billing-account-button.close`,
    defaultMessage: `Close`,
  },
  submitChange: {
    id: `marketplace-settings.gcp-change-billing-account-button.submit`,
    defaultMessage: `Change account`,
  },
  accountChangeSuccessTitle: {
    id: `marketplace-settings.gcp-change-billing-account-success-callout-title`,
    defaultMessage: `Account change successful!`,
  },
  newOrderNumber: {
    id: `marketplace-settings.gcp-change-billing-account-new-order-number`,
    defaultMessage: `New Order Number:`,
  },
  accountChangeFailTitle: {
    id: `marketplace-settings.gcp-change-billing-account-fail-callout-title`,
    defaultMessage: `Sorry, there was an error.`,
  },
})

interface SuccessResponseBody {
  // We're only interested in details.gcp_entitlement_id here
  details: { gcp_entitlement_id: string }
  [key: string]: any
}

interface ErrorResponseBody {
  message: string
}

const ChangeBillingAccountFlyout: FunctionComponent<AllProps> = (props) => {
  const { saasOrganization, onClose, fetchSaasOrganization, billingDetails } = props

  const [orderNumber, setOrderNumber] = useState('')
  const [billingAccountId, setBillingAccountId] = useState('')
  const [touchedOrderNumber, setTouchedOrderNumber] = useState(false)
  const [touchedBillingAccountId, setTouchedBillingAccountId] = useState(false)

  const validBillingAccountId = billingAccountId.trim().length > 0
  const validOrderNumber = orderNumber.trim().length > 0
  const isFormValid = validOrderNumber || validBillingAccountId
  const billingAccountRequest = useUpdateBillingAccount()

  const onSubmit = () => {
    billingAccountRequest.mutate(
      {
        pathParameters: { organization_id: saasOrganization.organization_id },
        body: {
          channel: 'gcp',
          details: {
            gcp_entitlement_id: orderNumber,
            gcp_billing_account_id: billingAccountId,
          },
        },
      },
      {
        onSuccess: () => {
          fetchSaasOrganization(saasOrganization.organization_id)
          billingDetails.refetch()
        },
      },
    )
  }

  const renderFlyoutHeader = () => (
    <EuiTitle size='m'>
      <h2 id={flyoutTitleId}>
        <FormattedMessage {...messages.changeBillingAccountTitle} />
      </h2>
    </EuiTitle>
  )

  const renderSuccessfulCallOut = () => {
    const data = billingAccountRequest.data as unknown as SuccessResponseBody
    const newOrderNumber = data.details.gcp_entitlement_id

    return (
      <Fragment>
        <EuiSpacer size='m' />
        <EuiCallOut
          title={<FormattedMessage {...messages.accountChangeSuccessTitle} />}
          color='success'
          iconType='user'
          data-test-id='success-callout'
        >
          {newOrderNumber ? (
            <EuiDescriptionList
              type='row'
              listItems={[
                {
                  title: <FormattedMessage {...messages.newOrderNumber} />,
                  description: newOrderNumber,
                },
              ]}
            />
          ) : null}
          <EuiSpacer size='m' />
          <EuiCodeBlock language='json' isCopyable={true} isVirtualized={true} overflowHeight={250}>
            {JSON.stringify(data, null, ' ')}
          </EuiCodeBlock>
        </EuiCallOut>
      </Fragment>
    )
  }

  const renderFailedCallOut = () => {
    const error = billingAccountRequest.error as ErrorResponseBody
    return (
      <Fragment>
        <EuiSpacer size='m' />

        <EuiCallOut
          title={<FormattedMessage {...messages.accountChangeFailTitle} />}
          color='danger'
          iconType='error'
          data-test-id='fail-callout'
        >
          {error.message}
        </EuiCallOut>
      </Fragment>
    )
  }

  const renderFlyoutBody = () => (
    <Fragment>
      <EuiSpacer />
      <EuiDescriptionList
        type='inline'
        listItems={[
          {
            title: <FormattedMessage {...messages.organizationId} />,
            description: saasOrganization.organization_id,
          },
        ]}
      />
      <EuiSpacer />
      <EuiText>
        <FormattedMessage {...messages.changeBillingAccountDescription} />
      </EuiText>
      <EuiSpacer />
      <EuiForm>
        <EuiFormRow
          label={<FormattedMessage {...messages.orderNumber} />}
          helpText={<FormattedMessage {...messages.orderNumberHelpText} />}
        >
          <EuiFieldText
            data-test-id='order-number-field-text'
            value={orderNumber}
            isInvalid={touchedOrderNumber && !(validOrderNumber || validBillingAccountId)}
            onChange={(e) => {
              setTouchedOrderNumber(true)
              setOrderNumber(e.target.value)
            }}
            readOnly={billingAccountRequest.isLoading || billingAccountRequest.isSuccess}
          />
        </EuiFormRow>

        <EuiFormRow
          label={<FormattedMessage {...messages.billingAccountId} />}
          helpText={<FormattedMessage {...messages.billingAccountIdHelpText} />}
        >
          <EuiFieldText
            data-test-id='billing-account-id-field-text'
            value={billingAccountId}
            isInvalid={touchedBillingAccountId && !(validOrderNumber || validBillingAccountId)}
            onChange={(e) => {
              setTouchedBillingAccountId(true)
              setBillingAccountId(e.target.value)
            }}
            readOnly={billingAccountRequest.isLoading || billingAccountRequest.isSuccess}
          />
        </EuiFormRow>
      </EuiForm>

      {billingAccountRequest.isSuccess && renderSuccessfulCallOut()}
      {billingAccountRequest.isError && renderFailedCallOut()}
    </Fragment>
  )

  const renderFlyoutFooter = () => (
    <EuiFlexGroup justifyContent='spaceBetween'>
      <EuiFlexItem grow={false}>
        <EuiButtonEmpty
          iconType='cross'
          onClick={onClose}
          flush='left'
          data-test-id='close-flyout-button'
        >
          {billingAccountRequest.isSuccess || billingAccountRequest.isError ? (
            <FormattedMessage {...messages.close} />
          ) : (
            <FormattedMessage {...messages.cancel} />
          )}
        </EuiButtonEmpty>
      </EuiFlexItem>

      <EuiFlexItem grow={false}>
        <EuiButton
          data-test-id='change-billing-account-button'
          disabled={!isFormValid || billingAccountRequest.isSuccess}
          onClick={onSubmit}
          isLoading={billingAccountRequest.isLoading}
          fill={true}
        >
          <FormattedMessage {...messages.submitChange} />
        </EuiButton>
      </EuiFlexItem>
    </EuiFlexGroup>
  )

  const flyoutTitleId = useGeneratedHtmlId({
    prefix: 'changeBillingAccountTitle',
  })

  return (
    <EuiFlyout
      size='m'
      maxWidth={0}
      ownFocus={true}
      onClose={onClose}
      aria-labelledby={flyoutTitleId}
      data-test-id='change-billing-account-flyout'
    >
      <EuiFlyoutHeader hasBorder={true}>{renderFlyoutHeader()}</EuiFlyoutHeader>
      <EuiFlyoutBody>{renderFlyoutBody()}</EuiFlyoutBody>
      <EuiFlyoutFooter>{renderFlyoutFooter()}</EuiFlyoutFooter>
    </EuiFlyout>
  )
}

export default ChangeBillingAccountFlyout
